import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export function useNotificationPreferences() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["notification-preferences", user?.id],
    queryFn: async () => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_account")
        .select("notification_preferences")
        .eq("external_auth_user", user.id)
        .single();

      if (error) throw error;
      return data?.notification_preferences || {};
    },
    enabled: !!user,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (preferences: any) => {
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("user_account")
        .update({ notification_preferences: preferences })
        .eq("external_auth_user", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-preferences"] });
      toast({
        title: "Preferences updated",
        description: "Notification preferences have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating preferences",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
